import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { pickAuthoredText, type Program } from "@heraion/shared";

import { useLocale } from "../primitives/useLocale";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import type { ConfirmFn, DurableWriteFn } from "../seams";
import { useFollows } from "../Library/useFollows";
import { useProgramsInvalidator } from "./programsInvalidator";

// The read-only gate for the PLAN surfaces (next/coaching.md, "La SPEC del
// follower", phase F3). The Library card got it first; the Plan tab and the
// web Plan mount read the same programs list, so a followed row would show
// up there with its pencil and its ✕ still live and every write behind them
// answering 404.
//
// ⛔ One way to read the follow set, and it is `useFollows`. This hook
// composes it, it does not re-fetch it.
//
// The confirm is the house `ConfirmFn` seam, injected by the host: mobile
// Alert, web window.confirm. ⛔ Not the Library's own Alert helper, which is
// mobile-only and speaks LibraryItem, while this surface is on both hosts
// and holds a Program.
//
// The copy is the SAME six `library.*` keys the Library card already uses.
// Two surfaces, one sentence: stopping to follow removes the caller's
// pointer and the author keeps the program.

export function useProgramFollowGate(opts: {
  apiFetch: ProgramApiFetch;
  /** Host write seam: mobile queues when offline, web sends only. */
  durableWrite: DurableWriteFn;
  /** Host confirm seam (seams.ts). Mobile Alert, web window.confirm. */
  confirm: ConfirmFn;
}) {
  const { t } = useTranslation();
  const lang = useLocale();
  const { followedProgramIds, sendsUnfollow, unfollow } = useFollows({
    apiFetch: opts.apiFetch,
    durableWrite: opts.durableWrite,
  });

  const { confirm } = opts;
  const invalidator = useProgramsInvalidator();

  /** Ask, then drop the follow row. Resolves null when the caller cancels
   *  (nothing happened) and null on success; the Error on failure, for the
   *  host to surface its own way. On success every usePrograms under the
   *  session's invalidator re-reads (#377, #380), the host's own included:
   *  after the DELETE, so the read no longer sees the follow. */
  const stopFollowing = useCallback(
    async (program: Program): Promise<Error | null> => {
      const name = pickAuthoredText(program.name, lang, program.authorLocale) ?? "";
      const ok = await confirm({
        title: t("library.unfollowTitle", {
          defaultValue: "Stop following?",
        }),
        body: t("library.unfollowBody", {
          name,
          defaultValue:
            "{{name}} stays with its author. This only removes it from your library.",
        }),
        confirmLabel: t("library.unfollow", {
          defaultValue: "Stop following",
        }),
        cancelLabel: t("mobile.workouts.queueDismissCancel", {
          defaultValue: "Cancel",
        }),
        // Nothing is destroyed: the author's program is untouched.
        destructive: false,
      });
      if (!ok) return null;
      // useFollows.unfollow answers null without a DELETE too: then there
      // is nothing to tell.
      const sends = sendsUnfollow("program", program.id);
      const err = await unfollow("program", program.id);
      if (sends && err === null) invalidator?.invalidate();
      return err;
    },
    [confirm, t, lang, unfollow, invalidator, sendsUnfollow],
  );

  return {
    /** Feeds `usePrograms({ followedProgramIds })`, which buckets these as
     *  followed whatever status their author left them in. The same set is
     *  what makes a card read-only. */
    followedProgramIds,
    stopFollowing,
  };
}
